import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../shared/Header';
import Footer from '../shared/Footer';
import SEO from '../shared/SEO';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#FCFBF9]">
      <SEO
        title="About"
        description="Learn about Crowns by Preshy — our story, values, and passion for premium hair styling. Dedicated to making every woman feel like royalty."
        path="/about"
      />
      <Header />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="font-serif text-4xl lg:text-6xl text-[#3E2723] mb-6 tracking-tight">About Crowns by Preshy</h1>
            <p className="text-[#786E66] text-lg max-w-3xl mx-auto leading-relaxed">
              Where every woman's beauty is celebrated, and every hairstyle tells a story.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.pexels.com/photos/853427/pexels-photo-853427.jpeg" 
                alt="Salon Interior"
                className="rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] w-full"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-serif text-3xl text-[#3E2723] mb-6 tracking-tight">Our Story</h2>
              <p className="text-[#1A1A1A] leading-relaxed mb-4">
                Crowns by Preshy was born from a passion for celebrating the natural beauty of African hair. Founded by Preshy, a master stylist with over a decade of experience, we've become a trusted name in premium hair styling.
              </p>
              <p className="text-[#786E66] leading-relaxed mb-4">
                What started as a small home-based service has grown into a full-service salon where women come not just for hairstyling, but for an experience that makes them feel like royalty.
              </p>
              <p className="text-[#786E66] leading-relaxed">
                Every braid, every weave, every style we create is crafted with care, precision, and a commitment to excellence that our clients have come to expect.
              </p>
            </motion.div>
          </div>

          <div className="bg-[#F3EBE1] rounded-3xl p-12 md:p-16 mb-24">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl text-[#3E2723] mb-4 tracking-tight">Our Values</h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Excellence',
                  desc: 'We never compromise on quality. Every hairstyle is executed with precision and attention to detail.'
                },
                {
                  title: 'Authenticity',
                  desc: 'We celebrate natural beauty and create styles that reflect each client\'s unique personality.'
                },
                {
                  title: 'Care',
                  desc: 'Beyond styling, we prioritize hair health and provide personalized care advice for every client.'
                }
              ].map((value, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <h3 className="font-serif text-xl text-[#3E2723] mb-3">{value.title}</h3>
                  <p className="text-[#786E66] leading-relaxed">{value.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-serif text-3xl text-[#3E2723] mb-6 tracking-tight">Meet Preshy</h2>
            <div className="max-w-3xl mx-auto">
              <p className="text-[#1A1A1A] leading-relaxed mb-4">
                Preshy is a certified master stylist with over 12 years of experience in African hair care and styling. Trained in both traditional and contemporary techniques, she combines artistry with technical skill to create stunning results.
              </p>
              <p className="text-[#786E66] leading-relaxed">
                Her passion lies in helping women embrace their natural beauty and feel confident in their own crown. When she's not styling, Preshy mentors aspiring stylists and advocates for healthy hair care practices.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
